import { Logger } from '../../common'

/* eslint-disable @typescript-eslint/no-explicit-any */
export function openIndexedDB(
  name: string,
  version: number,
  options?: { onUpgrade?: (db: IDBPDatabase, oldVersion: number, newVersion: number | null) => void },
): Promise<IDBPDatabase> {
  return new Promise((resolve, reject) => {
    const req = window.indexedDB.open(name, version)
    req.onupgradeneeded = options?.onUpgrade
      ? ev => {
          if (options?.onUpgrade) options.onUpgrade(new IDBPDatabase(req.result), ev.oldVersion, ev.newVersion)
        }
      : null
    req.onsuccess = () => resolve(new IDBPDatabase(req.result))
    req.onerror = () => reject(req.error)
  })
}

export class IDBPDatabase {
  constructor(private readonly db: IDBDatabase) {}
  createObjectStore(name: string, options?: IDBObjectStoreParameters): IDBPObjectStore {
    return new IDBPObjectStore(this.db.createObjectStore(name, options))
  }

  close() {
    this.db.close()
  }

  transaction(
    storeNames: string | string[],
    mode?: IDBTransactionMode,
    options?: IDBTransactionOptions,
  ): IDBPTransaction {
    return new IDBPTransaction(this.db.transaction(storeNames, mode, options))
  }

  get objectStoreNames(): string[] {
    const result: string[] = []
    for (let i = 0; i < this.db.objectStoreNames.length; i++) {
      const item = this.db.objectStoreNames.item(i)
      if (item) result.push(item)
    }
    return result
  }
}

interface IDBQueriable {
  count(query?: IDBValidKey | IDBKeyRange): IDBRequest<number>
  get(query: IDBValidKey | IDBKeyRange): IDBRequest<any>
  getAll(query?: IDBValidKey | IDBKeyRange | null, count?: number): IDBRequest<any[]>
  openCursor(
    query?: IDBValidKey | IDBKeyRange | null,
    direction?: IDBCursorDirection,
  ): IDBRequest<IDBCursorWithValue | null>
}

function promisifyRequest<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

function getAllImpl(
  queriable: IDBQueriable,
  query?: IDBValidKey | IDBKeyRange | null,
  options?: { from?: number; count?: number },
): Promise<any[]> {
  const from = options?.from ?? 0
  if (from > 0) {
    return new Promise(resolve => {
      const req = queriable.openCursor(query, 'next')
      let hasSkipped = false
      const items: any[] = []
      req.onsuccess = () => {
        const cursor = req.result
        if (cursor) {
          if (!hasSkipped) {
            cursor.advance(from)
            hasSkipped = true
            return
          }
          items.push(cursor.value)
          if (options?.count && items.length < options?.count) {
            resolve(items)
            return
          }
          cursor.continue()
        } else {
          resolve(items)
        }
      }
    })
  } else {
    return promisifyRequest(queriable.getAll(query, options?.count))
  }
}

function getImpl(queriable: IDBQueriable, query: IDBValidKey | IDBKeyRange): Promise<any> {
  return promisifyRequest(queriable.get(query))
}

export class IDBPObjectStore {
  constructor(private readonly store: IDBObjectStore) {}

  createIndex(name: string, keyPath: string | string[], options?: IDBIndexParameters) {
    this.store.createIndex(name, keyPath, options)
  }

  index(name: string) {
    return new IDBPIndex(this.store.index(name))
  }

  add(value: any, key?: IDBValidKey): Promise<IDBValidKey> {
    return promisifyRequest(this.store.add(value, key))
  }

  getAll(query?: IDBValidKey | IDBKeyRange | null, options?: { from?: number; count?: number }): Promise<any[]> {
    return getAllImpl(this.store, query, options)
  }

  get(query: IDBValidKey | IDBKeyRange): Promise<any> {
    return getImpl(this.store, query)
  }
}

export class IDBPTransaction {
  private static logger = new Logger('IDBPTransaction')

  onError?: (reason: any) => void

  constructor(private readonly transaction: IDBTransaction) {
    transaction.oncomplete = () => {
      // console.log('<65a2c420> Transaction completed');
    }
    transaction.onerror = () => {
      const msg = `<6e19059c> Transaction error: ${this.transaction.error}`
      IDBPTransaction.logger.error({}, msg)
      this.onError && this.onError(msg)
    }
  }

  abort(): void {
    this.transaction.abort()
  }

  commit(): void {
    this.transaction.commit()
  }

  objectStore(name: string): IDBPObjectStore {
    return new IDBPObjectStore(this.transaction.objectStore(name))
  }
}

export class IDBPIndex {
  constructor(private index: IDBIndex) {}

  getAll(query?: IDBValidKey | IDBKeyRange | null, options?: { from?: number; count?: number }): Promise<any[]> {
    return getAllImpl(this.index, query, options)
  }

  get(query: IDBValidKey | IDBKeyRange): Promise<any> {
    return getImpl(this.index, query)
  }
}
